import firebase from './firebase';
import { axiosGraphQL } from '../api';
import { isExplicitlyImpersonating } from './emailChange';

const REQUEST = `mutation RequestEmailChangeCode($email: String!, $language: String) {
  requestEmailChangeCode(email: $email, language: $language) { challengeId expiresAt resendAt }
}`;
const VERIFY = `mutation VerifyEmailChangeCode($challengeId: String!, $code: String!) {
  verifyEmailChangeCode(challengeId: $challengeId, code: $code) { user { _id email } customToken }
}`;
const errors = {
  EMAIL_SIGN_IN_INPUT_INVALID: 'invalid_email',
  EMAIL_SIGN_IN_CODE_INVALID: 'email_code_invalid',
  EMAIL_SIGN_IN_CODE_EXPIRED: 'email_code_expired',
  EMAIL_SIGN_IN_ATTEMPTS_EXHAUSTED: 'email_code_attempts_exhausted',
  EMAIL_SIGN_IN_RATE_LIMITED: 'email_code_rate_limited',
  EMAIL_SIGN_IN_DELIVERY_FAILED: 'email_change_code_failed',
  EMAIL_SIGN_IN_UNAVAILABLE: 'email_code_unavailable',
  email_change_already_in_use: 'email_change_already_in_use',
  email_change_same_address: 'email_change_same_address',
  email_change_auth_required: 'email_change_auth_required',
  Unauthorized: 'email_change_auth_required',
};
function publicFailure(error, verificationSubmitted = false) {
  const detail = error?.response?.data?.errors?.[0] || error;
  const failure = new Error(errors[detail?.extensions?.code || detail?.message] || 'email_code_unavailable');
  if (verificationSubmitted && failure.message === 'email_code_unavailable') failure.verificationOutcomeUnknown = true;
  const retryAt = Date.parse(detail?.extensions?.retryAt);
  if (Number.isFinite(retryAt)) failure.retryAt = retryAt;
  return failure;
}
export const getEmailChangeCodeError = error => [
  ...Object.values(errors), 'email_change_impersonation_forbidden', 'session_identity_changed',
].includes(error?.message) ? error.message : 'email_code_unavailable';

async function request(query, variables, field, uid) {
  if (isExplicitlyImpersonating()) throw new Error('email_change_impersonation_forbidden');
  const identity = firebase.auth().currentUser;
  if (!uid || identity?.uid !== uid) throw new Error('session_identity_changed');
  let response;
  let verificationSubmitted = false;
  try {
    const sessionToken = await identity.getIdToken();
    if (firebase.auth().currentUser?.uid !== uid) throw new Error('session_identity_changed');
    verificationSubmitted = field === 'verifyEmailChangeCode';
    response = await axiosGraphQL({ query, variables }, { sessionToken, expectedFirebaseUid: uid, withoutClub: true });
  } catch (error) {
    if (error?.message === 'session_identity_changed') throw error;
    throw publicFailure(error, verificationSubmitted);
  }
  if (firebase.auth().currentUser?.uid !== uid || isExplicitlyImpersonating()) throw new Error('session_identity_changed');
  if (response?.data?.errors?.length) throw publicFailure(response.data.errors[0], verificationSubmitted);
  const result = response?.data?.data?.[field];
  if (!result) throw publicFailure(new Error('email_code_unavailable'), verificationSubmitted);
  return result;
}
export async function requestEmailChangeCode(email, language, uid) {
  const result = await request(REQUEST, { email: email.trim().toLowerCase(), language }, 'requestEmailChangeCode', uid);
  const expires = Date.parse(result.expiresAt);
  const resend = Date.parse(result.resendAt);
  if (!/^[a-f0-9]{64}$/.test(result.challengeId) || !Number.isFinite(expires) ||
      !Number.isFinite(resend) || expires <= Date.now() || resend >= expires) throw new Error('email_code_unavailable');
  return result;
}
export async function verifyEmailChangeCode(challengeId, code, uid) {
  if (!/^\d{6}$/.test(code)) throw new Error('email_code_invalid');
  const verifyOnce = async () => {
    const result = await request(VERIFY, { challengeId, code }, 'verifyEmailChangeCode', uid);
    if (!result.user?._id || !result.user?.email || typeof result.customToken !== 'string' || !result.customToken) {
      throw publicFailure(new Error('email_code_unavailable'), true);
    }
    return result;
  };
  try { return await verifyOnce(); }
  catch (error) {
    if (!error.verificationOutcomeUnknown) throw error;
    // Core already supports a bounded retry of a consumed email-change challenge.
    try { return await verifyOnce(); }
    catch (retryError) {
      // Even expiry/exhaustion cannot disprove that the first request committed.
      retryError.verificationOutcomeUnknown = true;
      throw retryError;
    }
  }
}
